import React from 'react';
import Typography from 'antd/lib/typography';
import Space from 'antd/lib/space';
import Modal from 'antd/lib/modal';
import { FireOutlined, UndoOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { getIsGooglePlayStore, isAppPlatform } from '../../helpers';
import { selectRelease, selectAppStoreType, selectAppPlatform } from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import { ReleaseUpdatable } from '../../actions/updatable';
import { tryUpdateRelease, BaseUpdateReleaseRequest } from '../../actions/release';
import { StepRunUpdatable, ReleaseAppleVersionRequest } from '../../actions/stepRun';
import { RunwayResponseType } from '../../types';
import { StepRunComponentProps } from '../../typescriptHelpers';
import { useCallUpdatable, useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';
import { globalMessage } from '../../utils/globalMessage';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import PrepareHotfixOrRollbackReleaseModal from '../rollbacks/PrepareHotfixOrRollbackReleaseModal';
import { PrepareNewWorkflowRunModal } from '../prepareRelease/PrepareNewWorkflowRunModal';
import { runwayColors } from '../styles/globalStyles';
import { selectIsWorkflowsSSRStepsEnabled } from '../../selectors/selectors';
import { antGrey } from '../styles/globalStyles';

export interface RolloutActionButtonsProps {
    releaseId: string;
    appId: string;
    orgId: string;
    stepRunComponentProps?: StepRunComponentProps | null;
}

export const RolloutActionButtons: React.FC<RolloutActionButtonsProps> = ({
    releaseId,
    appId,
    orgId,
    stepRunComponentProps,
}) => {
    const dispatch = useDispatch();
    const [createHotfixModalVisible, setCreateHotfixModalVisible] = React.useState(false);
    const [rollbackReleaseId, setRollbackReleaseId] = React.useState(null);

    const rollbackInfo = useSelector((state: RootState) => {
        return selectRelease(state, releaseId).rollbackInfo;
    });

    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const isSSRMigrated = useSelector((state: RootState) => selectIsWorkflowsSSRStepsEnabled(state, appId));

    const isGooglePlayStore = getIsGooglePlayStore(appStoreType);
    const hasActiveRollback = !!rollbackInfo;
    const showDeployRollback = hasActiveRollback;
    const isConvertingRollback = hasActiveRollback;
    const userActionForRollbackDeploy =
        isGooglePlayStore ? RunwayUserAction.UASubmitAppUpdate : RunwayUserAction.UAReleaseAppUpdate;

    const [
        releaseAppleVersionStepRun,
        releaseAppleVersionIsLoadingStepRun,
        releaseAppleVersionResponseTypeStepRun,
        releaseAppleVersionErrorStepRun,
    ] = useCallUpdatable(StepRunUpdatable.ReleaseAppleVersion, stepRunComponentProps);

    const deployRollbackIsLoadingReleaseStep = useIsLoading(ReleaseUpdatable.ReleaseAppleVersion, rollbackReleaseId);
    const deployRollbackErrorReleaseStep = useError(ReleaseUpdatable.ReleaseAppleVersion, rollbackReleaseId);
    const deployRollbackSuccessReleaseStep = useUpdated(ReleaseUpdatable.ReleaseAppleVersion, rollbackReleaseId);

    const deployRollbackIsLoading =
        stepRunComponentProps ? releaseAppleVersionIsLoadingStepRun : deployRollbackIsLoadingReleaseStep;
    const deployRollbackError =
        stepRunComponentProps ? releaseAppleVersionErrorStepRun : deployRollbackErrorReleaseStep;
    const deployRollbackSuccess =
        stepRunComponentProps ?
            releaseAppleVersionResponseTypeStepRun === RunwayResponseType.Success
        :   deployRollbackSuccessReleaseStep;

    const submitRollbackIsLoading = useIsLoading(ReleaseUpdatable.SubmitAppForReview, rollbackReleaseId);
    const submitRollbackError = useUpdated(ReleaseUpdatable.SubmitAppForReview, rollbackReleaseId);
    const submitRollbackSuccess = useError(ReleaseUpdatable.SubmitAppForReview, rollbackReleaseId);

    const deployRollback = (releaseId: string) => {
        if (!isAppPlatform(platform)) {
            globalMessage.error('Cannot deploy rollback for this platform.');
            return;
        }

        if (stepRunComponentProps) {
            const request: ReleaseAppleVersionRequest = {
                releasePreviousSubmissionToAllUsersFirst: false,
            };
            releaseAppleVersionStepRun(request);
        } else {
            const request: BaseUpdateReleaseRequest = {
                orgId,
                appId,
                releaseId: releaseId,
            };
            dispatch(tryUpdateRelease(ReleaseUpdatable.ReleaseAppleVersion, request, releaseId));
        }

        setRollbackReleaseId(releaseId);
    };

    const showRollbackConfirm = () => {
        const content =
            isGooglePlayStore ?
                `This action will submit ${rollbackInfo.version} for review.`
            :   `This action will release the approved ${rollbackInfo.version} rollback.`;

        const verb = isGooglePlayStore ? 'submit' : 'deploy';

        Modal.confirm({
            title: `Are you sure you want to deploy the prepared rollback (${rollbackInfo.version})?`,
            content: content,
            okText: `Yes, ${verb} rollback`,
            okButtonProps: {
                type: 'primary',
            },
            cancelText: 'Nevermind',
            onOk: () => deployRollback(rollbackInfo.releaseId),
            maskClosable: true,
        });
    };

    React.useEffect(() => {
        if (deployRollbackSuccess) {
            globalMessage.success('Rollback has been deployed!');
        }
    }, [deployRollbackSuccess]);

    React.useEffect(() => {
        if (submitRollbackSuccess) {
            globalMessage.success('Rollback has been deployed!');
        }
    }, [submitRollbackSuccess]);

    React.useEffect(() => {
        if (deployRollbackError) {
            globalMessage.error(`Error deploying rollback: ${deployRollbackError}`);
        }
    }, [deployRollbackError]);

    React.useEffect(() => {
        if (submitRollbackError) {
            globalMessage.error(`Error deploying rollback: ${submitRollbackError}`);
        }
    }, [submitRollbackError]);

    return (
        <>
            <div
                style={{
                    padding: '18px',
                    borderRadius: 8,
                    border: `1px solid ${antGrey.grey5}`,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 12,
                    justifyContent: 'space-between',
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Space align="center">
                        <Typography.Text style={{ color: antGrey.grey7 }}>Fix actions</Typography.Text>
                    </Space>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <PermissionedActionButton
                        icon={<FireOutlined style={{ color: runwayColors.statusYellow }} />}
                        onClick={() => setCreateHotfixModalVisible(true)}
                        releaseId={releaseId}
                        style={{ width: '100%' }}
                    >
                        Start hotfix
                    </PermissionedActionButton>

                    {showDeployRollback && (
                        <PermissionedActionButton
                            disabled={!rollbackInfo?.isReadyToRelease}
                            onClick={showRollbackConfirm}
                            actionType={userActionForRollbackDeploy}
                            icon={<UndoOutlined style={{ color: runwayColors.statusRed }} />}
                            style={{ width: '100%' }}
                            loading={deployRollbackIsLoading || submitRollbackIsLoading}
                        >
                            Deploy rollback
                        </PermissionedActionButton>
                    )}
                </div>
            </div>

            {isSSRMigrated ?
                <PrepareNewWorkflowRunModal
                    orgId={orgId}
                    appId={appId}
                    open={createHotfixModalVisible}
                    mode="hotfix"
                    isConvertingRollback={isConvertingRollback}
                    onCancel={() => setCreateHotfixModalVisible(false)}
                    onSuccess={() => setCreateHotfixModalVisible(false)}
                />
            :   <PrepareHotfixOrRollbackReleaseModal
                    orgId={orgId}
                    appId={appId}
                    open={createHotfixModalVisible}
                    isConvertingRollback={isConvertingRollback}
                    onCancel={() => setCreateHotfixModalVisible(false)}
                />
            }
        </>
    );
};
